import { useTranslation } from "react-i18next";
import { useDocumentsLists } from "@/hooks/useDocumentsLists";
import { getDocIdsFromLists, isDocumentInSet } from "../../utils/documentUtils";
import { TalentDocumentsList } from "./TalentDocumentsList";
import { ApplicationDocumentsList } from "./ApplicationDocumentsList";

interface UploadedDocumentsSectionProps {
  documents: any[];
  talentProfileData: any;
  application_id?: string;
  canEditFile: (doc: any) => boolean;
  onView: (bucket: any, path: any) => void;
  onEdit: (doc: any) => void;
  onDelete: (id: number) => void;
}

export function UploadedDocumentsSection({
  documents,
  talentProfileData,
  application_id,
  canEditFile,
  onView,
  onEdit,
  onDelete
}: UploadedDocumentsSectionProps) {
  const { t } = useTranslation();
  const { data: documentsLists = [] } = useDocumentsLists();
  const reqDocIdsHelper = new Set<number>();
  const rTypes =
    talentProfileData?.required_documents?.required_document_types || [];
  const rLists =
    talentProfileData?.required_documents?.required_documents_lists || [];

  rTypes.forEach((id: number) => reqDocIdsHelper.add(id));

  const docsInLists = getDocIdsFromLists(rLists, documentsLists);
  docsInLists.forEach((id) => reqDocIdsHelper.add(id));

  // "Other" documents are documents NOT in reqDocIdsHelper
  const otherDocuments = documents.filter(
    (doc) => !isDocumentInSet(doc.document_type_id, reqDocIdsHelper)
  );

  if (otherDocuments.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic p-4 border rounded-md text-center">
        {t(
          "common:documents.no_documents",
          "Keine zusätzlichen Dokumente hochgeladen."
        )}
      </div>
    );
  }

  const talentDocuments = otherDocuments.filter((doc) => doc.type === "talent");
  const applicationDocuments = otherDocuments.filter(
    (doc) => doc.type === "application"
  );

  return (
    <>
      {talentDocuments.length > 0 && (
        <TalentDocumentsList
          documents={talentDocuments}
          application_id={application_id}
          canEditFile={canEditFile}
          onViewFile={onView}
          onEditDocument={onEdit}
          onDeleteDocument={onDelete}
        />
      )}
      {applicationDocuments.length > 0 && (
        <ApplicationDocumentsList
          documents={applicationDocuments}
          canEditFile={canEditFile}
          onViewFile={onView}
          onEditDocument={onEdit}
          onDeleteDocument={onDelete}
        />
      )}
    </>
  );
}
